import { useEffect, useState } from 'react'
import { Button, Col, Pagination, Row, Spinner, Table } from 'react-bootstrap'
import { humanizeDateAndSetArgTimezone } from '../../../../Utils/Dates/Dates'
import { getNewPlayerRequests } from '../../Api/Requests/Requests'
import { NewPlayerRequest } from '../../Api/Requests/models'
import { Title } from '../../../../Templates/Title'
import { BsPersonPlus } from 'react-icons/bs'
import { useSocket } from '../../../../hooks/useSocket'
import { useAdminInfo } from '../../../../hooks/useAdminInfo'
import { MdOpenInNew } from 'react-icons/md'
import { useNewPlayerRequestTable } from './useNewPlayerRequestTable'
import { OpenNewPlayerRequestModal } from './components/OpenNewPlayerRequestModal'
import Swal from 'sweetalert2'

interface Filters {
    status: string
    email: string
}

export const NewPlayerRequests = () => {
    // Hooks
    const { getStatusLabel, getTypeLabel, getStatusColor, getTypeColor } = useNewPlayerRequestTable()
    const { adminInfo } = useAdminInfo();
    const { socket } = useSocket();

    const [loading, setLoading] = useState(false)
    const [requests, setRequests] = useState<NewPlayerRequest[]>([])
    const [filters, setFilters] = useState<Filters>({ status: 'pending', email: '' })

    // Pagination
    const [totalCount, setTotalCount] = useState(0)
    const [totalPages, setTotalPages] = useState(0)
    const [currentPage, setCurrentPage] = useState(1)
    const [pageSize, setPageSize] = useState(30)

    const [showOpenNewPlayerRequestModal, setShowOpenNewPlayerRequestModal] = useState(false)
    const [selectedRequest, setSelectedRequest] = useState<NewPlayerRequest | null>(null)

    const fetchRequests = async ({ page = 1 }: { page: number }) => {
        const response = await getNewPlayerRequests({
            page: page,
            status: filters.status,
            email: filters.email
        })
        if (response?.success) {
            setRequests(response?.requests)
            setTotalCount(response?.totalCount)
            setTotalPages(Math.ceil(response?.totalCount / pageSize))
            setCurrentPage(response?.page)
            setLoading(false)
        }
    }

    const handlePageChange = (page: number) => {
        setCurrentPage(page)
        setLoading(true)
        fetchRequests({ page: page })
    }

    const handleOpenRequest = (request: NewPlayerRequest) => {
        setSelectedRequest(request)
        setShowOpenNewPlayerRequestModal(true)
    }

    useEffect(() => {
        fetchRequests({ page: 1 })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])


    useEffect(() => {
        if (socket) {
            socket.on('update-new-player-requests', (data: any) => {
                if (data?.OwnerId === adminInfo?.id) {
                    if (data?.request?.id === selectedRequest?.id) {
                        Swal.fire({
                            icon: 'info',
                            title: 'Solicitud actualizada',
                            text: 'La solicitud ya ha sido actualizada',
                        })
                        setSelectedRequest(null)
                        setShowOpenNewPlayerRequestModal(false)
                    }
                    fetchRequests({ page: currentPage })
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [socket, adminInfo]);

    const getRequestIsClosed = (request: NewPlayerRequest) => {
        return request.status !== 'pending'
    }

    useEffect(() => {
        if (!showOpenNewPlayerRequestModal) {
            setSelectedRequest(null)
        }
    }, [showOpenNewPlayerRequestModal])

    return (
        <div className='pb-5 mt-4'>
            {selectedRequest &&
                <OpenNewPlayerRequestModal
                    show={showOpenNewPlayerRequestModal}
                    onHide={() => setShowOpenNewPlayerRequestModal(false)}
                    request={selectedRequest}
                    fetchRequests={() => fetchRequests({ page: currentPage })}
                    
                />}
            <Title
                title="Solicitudes"
                icon={<BsPersonPlus size={30} />}
                subtitle="Solicitudes de nuevos jugadores"
            />
            <div>
                {loading &&
                    <div className='d-flex justify-content-center align-items-center flex-column text-primaryy' style={{ height: '20vh' }}>
                        <div className="mb-1">
                            Cargando página {currentPage}
                        </div>
                        <Spinner animation="border" variant="primary" />
                    </div>
                }
            </div>
            <Row md={12} className='justify-content-end align-items-end gap-2 my-3'>
                <Col md={3}>
                    <label className='text-sm text-primary'>Email</label>
                    <input type="text" className='form-control' onChange={(e) => setFilters({ ...filters, email: e.target.value })} placeholder='Busca por Email' />
                </Col>
                <Col md={3}>
                    <label className='text-sm text-primary'>Estado</label>
                    <select className='form-select' value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
                        <option value=''>Todos</option>
                        <option value='pending'>Pendiente</option>
                        <option value='approved'>Aprobado</option>
                        <option value='rejected'>Rechazado</option>
                    </select>
                </Col>

                <Col md={1} className='d-flex justify-content-end'>
                    <Button variant='secondary w-100' onClick={() => fetchRequests({ page: 1 })}>Buscar</Button>
                </Col>

            </Row>

            {!loading && requests?.length > 0 && <div>
                <Table striped bordered hover responsive>
                    <thead>
                        <tr>

                            <th className='text-sm text-primary text-center'>Fecha</th>
                            <th className='text-sm text-primary text-center'>Nombre</th>
                            <th className='text-sm text-primary text-center'>Email</th>
                            <th className='text-sm text-primary text-center'>Estado</th>
                            <th className='text-sm text-primary text-center'></th>

                        </tr>
                    </thead>
                    <tbody>
                        {requests?.map((row) => (
                            <tr key={row.id}>
                                <td className='text-sm text-secondary text-center'>{humanizeDateAndSetArgTimezone(row.createdAt)}</td>
                                <td className='text-sm text-primary text-center'>{row.firstName} {row.lastName}</td>
                                <td className='text-sm text-primary text-center'>{row.email}</td>
                                <td className={`text-sm text-center ${getStatusColor(row.status)}`}>{getStatusLabel(row.status)}</td>
                                <td className='text-sm text-primary text-center'>
                                    <Button onClick={() => handleOpenRequest(row)} variant={getRequestIsClosed(row) ? 'secondary' : 'primary'}><MdOpenInNew size={20} /></Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
                <div className='d-flex justify-content-center'>
                    <Pagination>
                        <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                        <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                        {currentPage > 3 && (
                            <>
                                <Pagination.Item onClick={() => handlePageChange(1)}>1</Pagination.Item>
                                <Pagination.Ellipsis disabled />
                            </>
                        )}
                        {[...Array(totalPages)].slice(
                            Math.max(0, currentPage - 3),
                            Math.min(totalPages, currentPage + 2)
                        ).map((_, index) => {
                            const pageNumber = Math.max(1, currentPage - 2) + index;
                            return (
                                <Pagination.Item
                                    key={pageNumber}
                                    active={currentPage === pageNumber}
                                    onClick={() => handlePageChange(pageNumber)}
                                >
                                    {pageNumber}
                                </Pagination.Item>
                            );
                        })}
                        {currentPage < totalPages - 2 && (
                            <>
                                <Pagination.Ellipsis disabled />
                                <Pagination.Item onClick={() => handlePageChange(totalPages)}>{totalPages}</Pagination.Item>
                            </>
                        )}
                        <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                        <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
                    </Pagination>
                </div>
            </div>}

            {!loading && requests?.length === 0 && <div className='d-flex justify-content-center align-items-center' style={{ height: '20vh' }}>
                <div className='text-primary'>No hay solicitudes.</div>
            </div>}
        </div>
    )
}
